import { create } from "zustand";
import { persist } from "zustand/middleware";
import { User, UserRole } from "@/services/types/common.types";
import TokenManager from "@/services/api/tokenManager";
import authService from "@/services/api/authService";
import { ApiException } from "@/services/api/axiosInstance";
import {
  UNSUPPORTED_ROLE_CODE,
  UNSUPPORTED_ROLE_MESSAGE,
} from "@/services/api/authMappers";
import i18n from "i18next";
import { toast } from "sonner";
import { queryClient } from "@/lib/react-query";
import { useContentStudioListStore } from "./contentStudioListStore";

// Module-level so React StrictMode's double effect (and remounts) trigger only one /me call
let sessionInitPromise: Promise<void> | null = null;

/**
 * Auth Store State Interface
 * Zustand holds: isAuthenticated, user (snapshot), error.
 * Token storage remains in TokenManager (used by API client).
 * API calls (login, register, refresh, logout, getCurrentUser) are done via TanStack Query.
 */
export interface AuthState {
  // User data (current logged-in user snapshot)
  user: User | null;

  // Authentication status (derived from tokens + user)
  isAuthenticated: boolean;
  isLoading: boolean;

  // Error state (e.g. from failed login)
  error: string | null;

  // Initial app load: true until we've determined auth state (tokens + me fetch)
  isInitializing: boolean;

  // Actions - state setters (called from React Query onSuccess / useAuthInit)
  setUser: (user: User | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setAuthenticated: (value: boolean) => void;
  setInitializing: (value: boolean) => void;

  // Clear auth state and tokens (called after logout API or on 401)
  logout: () => void;
  updateUser: (user: Partial<User>) => void;

  // Initialize auth state from TokenManager (has valid tokens?)
  initializeAuth: () => void;

  // Validate the session on app start via GET /me (single-flight, StrictMode safe)
  initializeSession: () => Promise<void>;

  // Derived helper
  hasRole: (role: UserRole) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isInitializing: true,

      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: !!user && TokenManager.hasValidTokens(),
          error: null,
        });
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      setAuthenticated: (value: boolean) => {
        set({ isAuthenticated: value });
      },

      setInitializing: (value: boolean) => {
        set({ isInitializing: value });
      },

      logout: () => {
        TokenManager.clearTokens();
        set({
          user: null,
          isAuthenticated: false,
          error: null,
          isLoading: false,
          isInitializing: false,
        });
        // Clear all TanStack Query cache so next user gets fresh API responses (no User A data for User B)
        queryClient.clear();
        // Persisted Content Studio list filters are per user: drop them too
        useContentStudioListStore.getState().reset();
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData },
          });
        }
      },

      initializeAuth: () => {
        const hasValidTokens = TokenManager.hasValidTokens();
        set({ isAuthenticated: hasValidTokens });
      },

      initializeSession: () => {
        if (sessionInitPromise) {
          return sessionInitPromise;
        }

        sessionInitPromise = (async () => {
          try {
            // No usable tokens: nothing to validate, no /me call
            if (!TokenManager.hasValidTokens()) {
              TokenManager.clearTokens();
              set({ user: null, isAuthenticated: false });
              return;
            }

            // /me is the source of truth; the interceptor refreshes the access token on 401.
            // /me has no email, so pass the persisted one to keep it (merge, never blank it out).
            const persistedEmail = get().user?.email;
            const user = await authService.getCurrentUser({
              email: persistedEmail,
            });
            set({ user, isAuthenticated: true, error: null });
          } catch (error) {
            const status = error instanceof ApiException ? error.status : 0;
            if (
              error instanceof ApiException &&
              error.code === UNSUPPORTED_ROLE_CODE
            ) {
              // Unsupported role: end the session (tokens cleared, so no retry loop) and say why
              const message = (i18n.t("common:toast.unsupportedRole") || UNSUPPORTED_ROLE_MESSAGE);
              get().logout();
              set({ error: message });
              toast.error(message);
            } else if (status === 401 || status === 403) {
              get().logout();
            } else if (get().user && TokenManager.hasValidTokens()) {
              // Transient failure (network/5xx): keep the persisted snapshot
              set({
                isAuthenticated: true,
                error:
                  error instanceof Error
                    ? error.message
                    : "Could not verify your session.",
              });
            } else {
              get().logout();
            }
          } finally {
            set({ isInitializing: false });
          }
        })();

        return sessionInitPromise;
      },

      hasRole: (role: UserRole) => {
        const user = get().user;
        return user?.roles?.includes(role) ?? false;
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        // do not persist isInitializing, isLoading, error
      }),
    },
  ),
);

// Selectors for derived helpers (Zustand doesn't support getters in persist, so we use selectors)
export const selectHasRole = (role: UserRole) => (state: AuthState) =>
  state.user?.roles?.includes(role) ?? false;
export const selectIsAdmin = (state: AuthState) =>
  state.user?.roles?.includes(UserRole.ADMIN) ?? false;
